import cn from 'classnames';
import React, { FC, useMemo } from 'react';
import { browserName, fullBrowserVersion, isDesktop, isMobile, isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button } from 'src/components/common/Button';
import { SvgIcon } from 'src/components/common/SvgIcon';
import { icons } from 'src/configs/icons';
import { getDeviceName } from 'src/helpers/utils';
import { RootState } from 'src/types/store-types';
import ReactPortal from './ReactPortal';
import './style.scss';

type ModalProps = {
  isOpen: boolean;
  hasOverlay?: boolean;
  handleClose?: () => void;
  handleOK?: () => void;
  handleCancel?: () => void;
  title?: string | any;
  description?: string;
  disabled?: boolean;
  children?: any;
  childerHasImage?: string;
  fullSize?: boolean;
  type?: 'default' | 'fullSize' | 'withImage' | 'small';
  icon?: any;
  id?: number | string;
  confirmText?: string;
  zIndex?: number;
  wrapperId?: number | string;
  className?: string;
  hideFooter?: boolean;
  showChatSupportBtn?: boolean;
};
const PopUpContainer: FC<ModalProps> = ({
  isOpen,
  handleClose,
  handleOK,
  handleCancel,
  title = '',
  disabled,
  children = <></>,
  hasOverlay = false,
  childerHasImage = '',
  description = '',
  type = 'default',
  icon,
  id = title,
  confirmText = 'ok',
  wrapperId = id || title,
  zIndex = 150,
  className = '',
  hideFooter = false,
  showChatSupportBtn = false,
}) => {
  const { t }: Translation = useTranslation();
  const { generalConfigs } = useSelector((state: RootState) => state.configs);

  const handleOpenChat = (): void => {
    if (generalConfigs?.chat?.type === 'zendesk') {
      (window as any)?.zE && (window as any)?.zE('messenger', 'open');
    } else if (['liveChatButton', 'liveChat'].includes(generalConfigs?.chat?.type)) {
      (window as any).LC_API.open_chat_window();
      const chatContainer = document.getElementById('chat-widget-container');
      if (chatContainer && isDesktop) {
        chatContainer.style.setProperty('z-index', '99999', 'important');
      }
    } else {
      const iframe = window.embeddedservice_bootstrap.utilAPI?.getEmbeddedMessagingFrame();

      window.embeddedservice_bootstrap?.prechatAPI?.setHiddenPrechatFields({
        Browser_Name: `${browserName} ${fullBrowserVersion}`,
        Device_Name: getDeviceName(),
      });

      window.embeddedservice_bootstrap.utilAPI?.launchChat().then(() => {
        if (iframe) {
          iframe.style.zIndex = 215;
          if (isMobile) {
            iframe.style.display = 'block';
          }
        }
      });
    }
  };

  const header = useMemo(() => {
    if (title) {
      return (
        <div
          className={cn('popup_heading_with_title', {
            ['withImage']: childerHasImage === 'withImage',
            ['withoutImage']: childerHasImage === 'withoutImage',
          })}
        >
          <h3 className={'popup_title'}>{title}</h3>
          {handleClose && (
            <div className={'popup_close'}>
              <SvgIcon src={icons.close} onClick={handleClose} />
            </div>
          )}
          {showChatSupportBtn && (
            <div className={'popup_help'}>
              <p>{t('contactSupport')}</p>
              <SvgIcon src={icons.chatIcon} className={'chat_icon'} onClick={handleOpenChat} />
            </div>
          )}
        </div>
      );
    }
    return (
      <div
        className={cn('popup_heading_without_title', {
          ['withImage']: childerHasImage === 'withImage',
          ['withoutImage']: childerHasImage === 'withoutImage',
        })}
      >
        {icon && <div className="popup_icon">{icon}</div>}
        {handleClose && (
          <div className={'popup_close'}>
            <SvgIcon src={icons.close} onClick={handleClose} />
          </div>
        )}
      </div>
    );
  }, [childerHasImage]);

  if (isOpen) {
    const resolvedWrapperId = typeof wrapperId === 'number' ? wrapperId.toString() : wrapperId;

    return (
      <ReactPortal wrapperId={resolvedWrapperId}>
        <div className="popup_container" style={{ zIndex: zIndex }}>
          <div className={cn('popup', type, className)}>
            {header}
            <div className="popup_body">
              <p className="popup_description" dangerouslySetInnerHTML={{ __html: description }} />
              {children}
            </div>
            {!hideFooter && (handleOK || handleCancel) && (
              <div className="popup_footer">
                {handleOK && (
                  <Button
                    className={cn('popup-btn success-filled-btn', { 'success-filled-btn--disabled': disabled })}
                    onClick={handleOK}
                    disabled={disabled}
                  >
                    {t(confirmText)}
                  </Button>
                )}
                {handleCancel && (
                  <Button className="popup-btn secondary-btn" onClick={handleCancel} disabled={disabled}>
                    {t('cancel')}
                  </Button>
                )}
              </div>
            )}
          </div>
          {(hasOverlay || isMobileOnly) && <div className="popup_container_backdrop" />}
        </div>
      </ReactPortal>
    );
  }
  return <></>;
};
export default PopUpContainer;
